import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Template} from "../../../../../components/template/template.jsx";
import Button from "../../../../../components/button/button.jsx";
import {FaEraser, FaFileAlt, FaPlus, FaSearch} from "react-icons/fa";
import {Card} from "../../../../../components/card/card.jsx";
import InputForm from "../../../../../components/input/input-form.jsx";
import {AgGridReact} from "ag-grid-react";
import aggridUtils from "../../../../../utils/aggrid-utils.js";
import {useQuery} from "@tanstack/react-query";
import {navigateHook} from "../../../../../hooks/use-navigate.js";
import {useGlobalKey} from "../../../../../hooks/use-global-key.js";
import {useFormNavigation} from "../../../../../hooks/use-form-navigation.js";
import materialDecreaseService from "../../../../../services/web/material-decrease-service.js";
import InputCheckbox from "../../../../../components/input/input-checkbox.jsx";
import SentryRow from "../../../../../components/template/sentry-row.jsx";
import CellRendererAttachButton from "../../../../../components/ag-grid/cell-renderer-attach-button.jsx";

const initialFilter = {
    material: "",
}

const pathModel = {
    create: {
        dev: "/materials/decreases/form",
        isBlank: false
    },
    edit: {
        dev: "/materials/decreases/form",
        isBlank: false
    }
}

const MaterialDecreasesList = () => {
    const navigate = navigateHook();
    const { onRowClicked, openForm } = useFormNavigation(pathModel);
    const [groupByMaterial, setGroupByMaterial] = useState(false)

    const [filter, setFilter] = useState(initialFilter);
    const [gridApi, setGridApi] = useState(null);
    const gridRef = useRef(null);

    const initialColumns = [
        {field: "id", hide: true},
        {field: "date", headerName: "Data/hora",  width: 145, comparator: aggridUtils.agGridCompareDateTime},
        {field: "storage", headerName: "Depósito", flex: 1},
        {field: "group", headerName: "Grupo", flex: 1},
        {field: "material", headerName: "Material", flex:1, filter: "agTextColumnFilter", filterParams: aggridUtils.filterParams},
        {field: "quantity", headerName: "Quantidade", width: 110},
        {field: "decrease_term", headerName: "Termo de baixa", width: 120,
            cellRenderer: ({data}) => {
                return <CellRendererAttachButton attachId={data.decrease_term} title="Termo de baixa" />
            }
        },
    ]

    const columnsGroup = [
        {field: "storage", headerName: "Depósito", flex: 1},
        {field: "group", headerName: "Grupo", flex: 1},
        {field: "material", headerName: "Material", flex:1, filter: "agTextColumnFilter", filterParams: aggridUtils.filterParams},
        {field: "quantity_total", headerName: "Total", width: 80},
    ]

    const [columnDefs, setColumns] = useState(initialColumns)

    useEffect(() => {
        if (groupByMaterial) {
            setColumns(columnsGroup)
        } else {
            setColumns(initialColumns)
        }
    }, [groupByMaterial]);

    const defaultColDef = useMemo(() => {
        return {
            suppressHeaderMenuButton: true,
            suppressHeaderFilterButton: true,
            suppressHeaderContextMenu: true,
            suppressFloatingFilterButton: true,
        };
    }, []);

    const { data: decreases = [], isLoading } = useQuery({
        queryKey: ['materials-decreases'],
        queryFn: async () => {
            const { data } = await materialDecreaseService.list();
            return data;
        },
    });

    const onChange = useCallback(({ target: { id, value } }) => {
        setFilter(prev => ({ ...prev, [id]: value }));
    }, []);

    const search = useCallback(() => {
        if (!gridApi) return;

        const filterModel = {};

        Object.entries(filter).forEach(([field, value]) => {
            if (!value) return;
            filterModel[field] = {
                filterType: "text",
                type: "contains",
                filter: value
            }
        });

        gridApi.setFilterModel(filterModel);
        gridApi.onFilterChanged();
    }, [filter, gridApi]);

    const clearFilter = useCallback(() => {
        setFilter(initialFilter);
        const api = gridRef.current?.api;
        if (api) {
            api.setFilterModel(null);
            api.onFilterChanged();
        }
    }, []);

    useGlobalKey("Enter", search);
    useGlobalKey("Escape", clearFilter);

    const openReport = () => {
        const url = `/reports/material_movements?operation=decrease`;
        navigate(url, true)
    }

    const openEditForm = (e) => {
        if(!e.event.target.closest("BUTTON")) {
            onRowClicked(e)
        }
    }

    const getRowData = () => {
        const materialsGrouped = Object.values(
            decreases.reduce((acc, item) => {
                const key = `${item.material_id}_${item.group_id}_${item.storage_id}`;

                if (!acc[key]) {
                    acc[key] = {
                        material: item.material,
                        group: item.group,
                        storage: item.storage,
                        quantity_total: 0
                    };
                }

                acc[key].quantity_total += item.quantity;

                return acc;
            }, {})
        );

        return groupByMaterial ? materialsGrouped : decreases;
    }

    return (
        <Template isLoading={isLoading}>
            <Template.Header>
                <Template.Title>
                    Baixas
                </Template.Title>
                <div className={"flex gap-2"}>
                    <Button onClick={openReport}>
                        <FaFileAlt/><span className="font-bold">Relatório</span>
                    </Button>
                    <Button onClick={openForm}>
                        <FaPlus/><span className="font-bold">Adicionar baixa</span>
                    </Button>
                </div>
            </Template.Header>
            <Template.Body>
                <Card>
                    <Card.Body>
                        <SentryRow>
                            <div className={"col-span-12 flex gap-4"}>
                                <InputForm id="material" name="material" value={filter.material} onChange={onChange}
                                           label="Material" classNameWrapper={"w-full"}/>
                                <div className="flex justify-start gap-2">
                                    <Button target="primary-outline" onClick={clearFilter} title={"Limpar"}>
                                        <FaEraser/>
                                    </Button>
                                    <Button onClick={search} title={"Buscar"}><FaSearch/></Button>
                                </div>
                            </div>
                        </SentryRow>
                    </Card.Body>
                </Card>
                <Card className="mt-2">
                    <Card.Body>
                        <div>
                            <InputCheckbox
                                id="group-material"
                                className={"mb-2"}
                                checked={groupByMaterial}
                                label="Agrupar por material"
                                onChange={({target: {checked}}) => setGroupByMaterial(checked)}/>
                        </div>
                        <div className={"h-[calc(100vh-181px)] aggrid-row-pointer"}>
                            <AgGridReact
                                onGridReady={(params) => {
                                    setGridApi(params.api);
                                }}
                                ref={gridRef}
                                rowData={getRowData()}
                                columnDefs={columnDefs}
                                defaultColDef={defaultColDef}
                                pagination
                                onRowClicked={openEditForm}
                                paginationPageSize={50}
                                paginationPageSizeSelector={[20, 50, 100, 500]}
                                animateRows={false}
                                suppressCellFocus={true}
                                gridOptions={{localeText: aggridUtils.localization()}}
                                theme={aggridUtils.generalTheme(40)}
                                onFilterChanged={aggridUtils.filterChangedNoRowsOverlay}
                            />
                        </div>
                    </Card.Body>
                </Card>
            </Template.Body>
        </Template>
    )
};

export default MaterialDecreasesList;